import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Receipt, SearchCheck, BookOpenCheck, Building2, ShieldCheck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { SERVICE_GROUPS, ALL_SERVICE_COUNT } from "@/data/services";
import { IMAGES } from "@/data/content";

const GROUP_ICONS = { receipt: Receipt, search: SearchCheck, book: BookOpenCheck, building: Building2 };

export default function Services() {
  const { t, isAr } = useLang();
  const Arrow = ({ className = "" }) => <ArrowRight className={`${className} ${isAr ? "rotate-180" : ""}`} />;

  return (
    <div className="bg-ivory">
      <section className="relative overflow-hidden border-b border-line" data-testid="services-hero">
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: `url(${IMAGES.brandTexture})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-ivory/50 to-ivory" />
        <div className="container-tnsq relative py-20 lg:py-24">
          <Reveal>
            <div className="text-sm text-muted mb-5">
              <Link to="/" className="hover:text-navy">{t({ en: "Home", ar: "الرئيسية" })}</Link>
              <span className="mx-2">/</span>
              <span className="text-ink font-medium">{t({ en: "Services", ar: "الخدمات" })}</span>
            </div>
          </Reveal>
          <Reveal delay={80}>
            <span className="kicker gold-line">{t({ en: "What we do", ar: "ما نقدمه" })}</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.1] max-w-4xl mt-4">
              {t({ en: "One firm for tax, audit, accounting and company setup", ar: "شركة واحدة للضرائب والتدقيق والمحاسبة وتأسيس الشركات" })}
            </h1>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-6 text-muted text-base md:text-lg max-w-2xl leading-relaxed">
              {t({ en: `A complete, integrated catalogue of ${ALL_SERVICE_COUNT}+ services for businesses in the UAE and beyond — from VAT and Corporate Tax to audit, bookkeeping, company formation, visas and bank accounts.`, ar: `دليل خدمات متكامل يضم أكثر من ${ALL_SERVICE_COUNT} خدمة للأعمال في الإمارات وخارجها — من ضريبة القيمة المضافة وضريبة الشركات إلى التدقيق ومسك الدفاتر وتأسيس الشركات والتأشيرات والحسابات البنكية.` })}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="services-detail">
        <div className="container-tnsq grid lg:grid-cols-[260px_1fr] gap-14">
          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-1">
              <p className="kicker mb-4">{t({ en: "Categories", ar: "الأقسام" })}</p>
              {SERVICE_GROUPS.map((g) => {
                const Icon = GROUP_ICONS[g.icon] || ShieldCheck;
                return (
                  <a key={g.id} href={`#${g.id}`} data-testid={`group-nav-${g.id}`}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted hover:text-navy hover:bg-surface transition-colors">
                    <Icon className="h-4 w-4 text-brandgreen" /> {t(g.label)}
                  </a>
                );
              })}
            </div>
          </aside>

          <div className="space-y-20">
            {SERVICE_GROUPS.map((g) => {
              const Icon = GROUP_ICONS[g.icon] || ShieldCheck;
              return (
                <div key={g.id} id={g.id} className="scroll-mt-28">
                  <Reveal>
                    <div className="flex items-start gap-4 mb-8">
                      <div className="h-12 w-12 grid place-items-center rounded-xl bg-navy text-white shrink-0"><Icon className="h-6 w-6" /></div>
                      <div>
                        <h2 className="font-display text-2xl sm:text-3xl text-ink leading-tight">{t(g.label)}</h2>
                        <p className="mt-2 text-muted leading-relaxed max-w-2xl">{t(g.blurb)}</p>
                      </div>
                    </div>
                  </Reveal>

                  <div className="grid md:grid-cols-2 gap-6">
                    {g.services.map((s, i) => (
                      <Reveal key={s.id} delay={(i % 2) * 90} className="h-full">
                        <article id={s.id} data-testid={`service-detail-${s.id}`}
                          className="scroll-mt-28 flex h-full flex-col rounded-2xl border border-line bg-white p-7 shadow-card hover:shadow-lift transition-shadow duration-300">
                          <h3 className="font-display text-xl text-ink">{t(s.title)}</h3>
                          <p className="mt-3 text-sm text-muted leading-relaxed">{t(s.summary)}</p>
                          <ul className="mt-5 space-y-2.5">
                            {t(s.points).map((p, k) => (
                              <li key={k} className="flex gap-3 items-start">
                                <span className="h-5 w-5 grid place-items-center rounded-full bg-brandgreen/10 text-brandgreen shrink-0 mt-0.5"><Check className="h-3 w-3" /></span>
                                <span className="text-sm text-ink/75 leading-relaxed">{p}</span>
                              </li>
                            ))}
                          </ul>
                          <Link to="/#contact" data-testid={`service-enquire-${s.id}`}
                            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-navy hover:gap-3 transition-all">
                            {t({ en: "Enquire about this service", ar: "استفسر عن هذه الخدمة" })} <Arrow className="h-4 w-4" />
                          </Link>
                        </article>
                      </Reveal>
                    ))}
                  </div>
                </div>
              );
            })}

            <Reveal>
              <div className="relative overflow-hidden rounded-[1.75rem] bg-navy-deep p-8 md:p-12 flex flex-col md:flex-row md:items-center gap-6 justify-between">
                <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${IMAGES.brandTexture})`, backgroundSize: "cover" }} />
                <div className="relative">
                  <h3 className="font-display text-2xl md:text-3xl text-white">{t({ en: "Not sure which service fits?", ar: "غير متأكد أي خدمة تناسبك؟" })}</h3>
                  <p className="mt-2 text-white/70">{t({ en: "Send the basic facts and we'll point you to the right next step.", ar: "أرسل البيانات الأساسية ونوجّهك للخطوة الصحيحة." })}</p>
                </div>
                <Link to="/#contact" data-testid="services-cta-contact"
                  className="relative inline-flex items-center gap-2 h-13 px-7 py-3.5 rounded-full bg-gold text-navy-deep font-semibold hover:bg-white transition-colors shrink-0">
                  {t({ en: "Request a consultation", ar: "اطلب استشارة" })} <Arrow className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
